import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

const HOME_CARDS_DISMISSED_COOKIE = 'darveys_mobile_menu_home_cards_dismissed';
const HOME_CARDS_COOKIE_MAX_AGE = 60 * 60 * 24 * 400;
const HOME_BANNER_CLICKED_KEY = 'home_banner_clicked';
const HOME_BANNER_SELECTOR = '.mobile-mega-menu__panel-body--home';
const HOME_BANNER_DISMISSED_CLASS = 'mobile-menu-home-dismissed';
const DRAWER_READY_CLASS = 'mobile-menu-state-ready';
const TABS_PANEL_ID = 'mobile-menu-tabs';
const NAVIGATION_STATE_KEY = 'darveys_mobile_menu_navigation_state';
const ACTIVE_TAB_STATE_KEY = 'active_mega_menu_tab';
const TAB_TRIGGER_SELECTOR = '[data-tab-id]';

/**
 * Mobile mega menu drawer controller.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  /** @type {string[]} */
  #panelStack = [];

  /** @type {string} */
  #lastActiveTab = '';

  /** @type {{ panelId: string, panelStack: string[], tabId: string } | null} */
  #lastNavigationState = null;

  /** @type {number} */
  #restorationAnimationFrame = 0;

  /** @type {number} */
  #pageShowAnimationFrame = 0;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.querySelector(HOME_BANNER_SELECTOR)?.addEventListener('click', this.#onHomeBannerClick);
    window.addEventListener('pageshow', this.#onPageShow);
    window.addEventListener('pagehide', this.#onPageHide);
    this.#applyHomeBannerVisibility();
    this.#resetPanels();
    this.#restoreNavigationState();
    this.classList.add(DRAWER_READY_CLASS);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.querySelector(HOME_BANNER_SELECTOR)?.removeEventListener('click', this.#onHomeBannerClick);
    window.removeEventListener('pageshow', this.#onPageShow);
    window.removeEventListener('pagehide', this.#onPageHide);
    this.#cancelPageShowReset();
    this.#cancelScheduledSessionRestoration();
    this.classList.remove(DRAWER_READY_CLASS);
  }

  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  get defaultPanel() {
    return this.dataset.defaultPanel ?? 'mobile-menu-home';
  }

  get defaultTab() {
    return this.dataset.defaultTab ?? '';
  }

  get stateNamespace() {
    return this.dataset.stateNamespace || 'header-menu';
  }

  get initialPanel() {
    if (!isHomeBannerClicked() && !getCookie(HOME_CARDS_DISMISSED_COOKIE)) return this.defaultPanel;

    const tabsPanel = this.querySelector(`[data-panel="${TABS_PANEL_ID}"]`);
    return tabsPanel ? TABS_PANEL_ID : this.defaultPanel;
  }

  get initialTab() {
    return (
      this.#getValidTabId(this.#lastActiveTab) ||
      this.#readActiveTabState() ||
      getCookie(HOME_CARDS_DISMISSED_COOKIE) ||
      this.defaultTab
    );
  }

  /**
   * @returns {HTMLElement[]}
   */
  get panels() {
    return Array.from(this.querySelectorAll('[data-panel]'));
  }

  /**
   * @returns {string | null}
   */
  get activePanelId() {
    return this.querySelector('[data-panel].is-active')?.getAttribute('data-panel') ?? null;
  }

  /**
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    const activePanelId = this.activePanelId;
    if (activePanelId && activePanelId !== this.initialPanel) {
      this.back();
      return;
    }

    this.close();
  };

  /**
   * @param {Event} event
   */
  #onHomeBannerClick = (event) => {
    const trigger = event.target instanceof Element ? event.target.closest('[data-target-panel="mobile-menu-tabs"]') : null;
    if (!trigger) return;

    setHomeBannerClicked();
    this.#applyHomeBannerVisibility();
  };

  /**
   * @param {PageTransitionEvent} event
   */
  #onPageShow = (event) => {
    if (!event.persisted) return;

    // Correct the BFcache snapshot synchronously, then enforce it again before the next paint.
    this.#resetDrawerState();
    this.#cancelPageShowReset();
    this.#pageShowAnimationFrame = requestAnimationFrame(() => {
      this.#pageShowAnimationFrame = 0;
      this.#resetDrawerState();
    });
  };

  #onPageHide = () => {
    this.#persistNavigationState();
    this.#cancelPageShowReset();
    this.#cancelScheduledSessionRestoration();
    this.#applyHomeBannerVisibility();
    reset(this.refs.details);
    removeTrapFocus();
  };

  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Allows the native Shopify account dialog to take focus from the mobile drawer.
   */
  prepareAccountDialog() {
    removeTrapFocus();
  }

  open() {
    this.#cancelPageShowReset();
    this.#applyHomeBannerVisibility();

    const summary = this.refs.details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');
    this.#resetPanels();
    this.#restoreNavigationState();
    window.DarveysMobileScrollLock?.refresh();

    requestAnimationFrame(() => {
      this.refs.details.classList.add('menu-open');
      window.DarveysMobileScrollLock?.refresh();
      setTimeout(() => {
        trapFocus(this.refs.details);
      }, 0);
    });
  }

  /**
   * @param {Event} event
   */
  showPanel(event) {
    this.#cancelScheduledSessionRestoration();

    const trigger = event.target instanceof Element ? event.target.closest('[data-target-panel]') : null;
    const targetPanel = trigger?.getAttribute('data-target-panel');

    if (!targetPanel) return;

    const tab = trigger?.getAttribute('data-tab');
    const parentTabId = this.#getParentTabId(targetPanel);
    const nextTabId = parentTabId || this.#getValidTabId(tab ?? '');
    const isHomeCardSelection = Boolean(trigger?.closest('.mobile-mega-menu__home-cards'));
    if (isHomeCardSelection) {
      setHomeBannerClicked();
      this.#applyHomeBannerVisibility();
      setCookie(HOME_CARDS_DISMISSED_COOKIE, tab || 'true', HOME_CARDS_COOKIE_MAX_AGE);
      this.#panelStack = [];
    }

    const currentPanelId = this.activePanelId;
    if (!isHomeCardSelection && currentPanelId && currentPanelId !== targetPanel) {
      this.#panelStack.push(currentPanelId);
    }

    if (nextTabId) {
      this.#writeActiveTabState(nextTabId);
      this.#activateTab(nextTabId);
    }

    this.#activatePanel(targetPanel);

    this.#persistNavigationState();
  }

  /**
   * @param {Event} event
   */
  switchTab(event) {
    const button = event.target instanceof Element ? event.target.closest(TAB_TRIGGER_SELECTOR) : null;
    const tab = button?.getAttribute('data-tab-id');

    if (!tab) return;

    this.#cancelScheduledSessionRestoration();
    this.#writeActiveTabState(tab);
    this.#activateTab(tab);
    this.#persistNavigationState();
  }

  back() {
    this.#cancelScheduledSessionRestoration();
    const previousPanelId = this.#panelStack.pop() ?? this.initialPanel;
    this.#activatePanel(previousPanelId);
    this.#persistNavigationState();
  }

  close() {
    this.#persistNavigationState();
    this.#cancelScheduledSessionRestoration();

    const summary = this.refs.details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    this.refs.details.classList.remove('menu-open');

    onAnimationEnd(this.refs.details, () => {
      reset(this.refs.details);
      removeTrapFocus();
      window.DarveysMobileScrollLock?.refresh();
    });
  }

  /**
   * @param {Event} event
   */
  filterRegions(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    const panel = input.closest('[data-panel]');
    if (!(panel instanceof HTMLElement)) return;

    const query = input.value.trim().toLowerCase();
    const items = panel.querySelectorAll('[data-region-item]');

    items.forEach((item) => {
      if (!(item instanceof HTMLElement)) return;
      const name = item.dataset.regionName ?? '';
      const currency = item.dataset.regionCurrency ?? '';
      item.hidden = query !== '' && !name.includes(query) && !currency.includes(query);
    });
  }

  /**
   * @param {string} panelId
   */
  #activatePanel(panelId) {
    this.panels.forEach((panel) => {
      const isActive = panel.getAttribute('data-panel') === panelId;
      panel.classList.toggle('is-active', isActive);
      panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });
  }

  /**
   * @param {string} tabId
   * @param {boolean} [force]
   * @returns {boolean}
   */
  #activateTab(tabId, force = false) {
    const escapedTabId = CSS.escape(tabId);
    const targetTab = this.querySelector(`[data-tab-id="${escapedTabId}"]`);
    const targetPanel = this.querySelector(`[data-tab-panel="${escapedTabId}"]`);

    if ((!targetTab || !(targetPanel instanceof HTMLElement)) && !force) return false;

    const tabButtons = this.querySelectorAll(TAB_TRIGGER_SELECTOR);
    const tabPanels = this.querySelectorAll('[data-tab-panel]');

    tabButtons.forEach((button) => {
      if (!(button instanceof HTMLElement)) return;
      button.classList.remove('is-active');
      button.setAttribute('aria-selected', 'false');
    });

    tabPanels.forEach((panel) => {
      if (!(panel instanceof HTMLElement)) return;
      panel.classList.remove('is-active');
      panel.hidden = true;
    });

    if (!(targetTab instanceof HTMLElement) || !(targetPanel instanceof HTMLElement)) return false;

    if (force) void targetTab.offsetWidth;

    targetTab.classList.add('is-active');
    targetTab.setAttribute('aria-selected', 'true');
    targetPanel.classList.add('is-active');
    targetPanel.classList.add('feedback');

    setTimeout(() => {
      targetPanel.classList.remove('feedback');
    }, 500);
    targetPanel.hidden = false;
    this.#lastActiveTab = tabId;

    return true;
  }

  /**
   * @param {boolean} [forceTabsPanel]
   */
  #resetPanels(forceTabsPanel = false) {
    this.#panelStack = [];
    this.#clearPanelState();

    const tabsPanelExists = Boolean(this.querySelector(`[data-panel="${TABS_PANEL_ID}"]`));
    const targetPanel = forceTabsPanel && tabsPanelExists ? TABS_PANEL_ID : this.initialPanel;
    this.#activatePanel(targetPanel);

    const didActivateInitialTab = this.initialTab ? this.#activateTab(this.initialTab) : false;
    if (!didActivateInitialTab) {
      const firstTabButton = this.querySelector(TAB_TRIGGER_SELECTOR);
      const firstTabId = firstTabButton?.getAttribute('data-tab-id');
      if (firstTabId) {
        this.#activateTab(firstTabId);
      }
    }

    const regionInputs = this.querySelectorAll('.mobile-mega-menu__region-input');
    regionInputs.forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      input.value = '';
    });

    this.querySelectorAll('[data-region-item]').forEach((item) => {
      if (item instanceof HTMLElement) item.hidden = false;
    });
  }

  #clearPanelState() {
    this.panels.forEach((panel) => {
      panel.classList.remove('is-active', 'opened-panel');
      panel.setAttribute('aria-hidden', 'true');
    });

    this.querySelectorAll(TAB_TRIGGER_SELECTOR).forEach((button) => {
      button.classList.remove('is-active', 'opened-panel');
      button.setAttribute('aria-selected', 'false');
    });

    this.querySelectorAll('[data-tab-panel]').forEach((panel) => {
      panel.classList.remove('is-active', 'opened-panel');
      if (panel instanceof HTMLElement) panel.hidden = true;
    });

    this.querySelectorAll('.opened-panel').forEach((element) => {
      element.classList.remove('opened-panel');
    });
  }

  #persistNavigationState() {
    const panelId = this.activePanelId;

    if (!panelId || panelId === this.defaultPanel || panelId === TABS_PANEL_ID) {
      this.#clearNavigationState();
      return;
    }

    const activeTabId = this.#resolveActiveTabId(panelId);
    if (activeTabId) {
      this.#writeActiveTabState(activeTabId);
    }

    const navigationState = {
      panelId,
      panelStack: [...this.#panelStack],
      tabId: activeTabId,
    };

    this.#lastNavigationState = navigationState;

    try {
      sessionStorage.setItem(this.#getSessionKey(NAVIGATION_STATE_KEY), JSON.stringify(navigationState));
    } catch {
      // The private property still preserves state for the current document.
    }
  }

  /**
   * @returns {boolean}
   */
  #restoreNavigationState() {
    const navigationState = this.#readNavigationState();
    if (!navigationState) return false;

    const targetPanelExists = this.panels.some(
      (panel) => panel.getAttribute('data-panel') === navigationState.panelId
    );

    if (!targetPanelExists || navigationState.panelId === this.defaultPanel || navigationState.panelId === TABS_PANEL_ID) {
      this.#clearNavigationState();
      return false;
    }

    const availablePanelIds = new Set(
      this.panels.map((panel) => panel.getAttribute('data-panel')).filter((panelId) => Boolean(panelId))
    );

    this.#clearPanelState();
    this.#panelStack = navigationState.panelStack.filter(
      (panelId) => availablePanelIds.has(panelId) && panelId !== navigationState.panelId
    );
    this.#activatePanel(navigationState.panelId);

    // The panel's explicit data-parent-tab binding overrides potentially stale
    // BFcache classes and the server-rendered first-tab default.
    const parentTabId = this.#getParentTabId(navigationState.panelId);
    const tabIdToRestore =
      parentTabId || this.#getValidTabId(navigationState.tabId) || this.#readActiveTabState();
    const didRestoreTab = tabIdToRestore ? this.#activateTab(tabIdToRestore, true) : false;
    if (!didRestoreTab) {
      const firstTabButton = this.querySelector(TAB_TRIGGER_SELECTOR);
      const firstTabId = firstTabButton?.getAttribute('data-tab-id');
      if (firstTabId) {
        this.#activateTab(firstTabId, true);
      }
    }

    // Rewrite legacy/stale state with the tab inferred from the restored submenu.
    this.#persistNavigationState();
    this.#scheduleSessionRestoration(navigationState.panelId, this.#lastActiveTab);

    return true;
  }

  /**
   * Reapply the restored state after the theme's mount/open layout passes.
   * @param {string} panelId
   * @param {string} tabId
   */
  #scheduleSessionRestoration(panelId, tabId) {
    if (!tabId) return;

    if (this.#restorationAnimationFrame) {
      cancelAnimationFrame(this.#restorationAnimationFrame);
    }

    this.#restorationAnimationFrame = requestAnimationFrame(() => {
      this.#restorationAnimationFrame = requestAnimationFrame(() => {
        this.#restorationAnimationFrame = 0;

        const navigationState = this.#readNavigationState();
        if (!navigationState || navigationState.panelId !== panelId) return;

        this.#activatePanel(panelId);
        this.#activateTab(tabId, true);
        this.#persistNavigationState();
      });
    });
  }

  #cancelScheduledSessionRestoration() {
    if (this.#restorationAnimationFrame) {
      cancelAnimationFrame(this.#restorationAnimationFrame);
      this.#restorationAnimationFrame = 0;
    }
  }

  #cancelPageShowReset() {
    if (this.#pageShowAnimationFrame) {
      cancelAnimationFrame(this.#pageShowAnimationFrame);
      this.#pageShowAnimationFrame = 0;
    }
  }

  /**
   * @param {string} key
   * @returns {string}
   */
  #getSessionKey(key) {
    return `${key}:${this.stateNamespace}`;
  }

  /**
   * @param {string} panelId
   * @returns {string}
   */
  #resolveActiveTabId(panelId) {
    const parentTabId = this.#getParentTabId(panelId);
    if (parentTabId) return parentTabId;

    const selectedButton = this.querySelector('[data-tab-id][aria-selected="true"]');
    const selectedTabId = selectedButton?.getAttribute('data-tab-id') ?? '';
    if (this.#getValidTabId(selectedTabId)) return selectedTabId;

    const storedTabId = this.#readActiveTabState();
    if (storedTabId) return storedTabId;

    if (this.#getValidTabId(this.#lastActiveTab)) return this.#lastActiveTab;

    const activeButton = this.querySelector('[data-tab-id].is-active');
    return this.#getValidTabId(activeButton?.getAttribute('data-tab-id') ?? '');
  }

  /**
   * @param {string} panelId
   * @returns {string}
   */
  #getParentTabId(panelId) {
    const panel = this.panels.find((candidate) => candidate.getAttribute('data-panel') === panelId);
    return this.#getValidTabId(panel?.getAttribute('data-parent-tab') ?? '');
  }

  /**
   * @param {string} tabId
   * @returns {string}
   */
  #getValidTabId(tabId) {
    if (!tabId) return '';

    const matchingButton = this.querySelector(`[data-tab-id="${CSS.escape(tabId)}"]`);
    return matchingButton ? tabId : '';
  }

  /**
   * @param {string} tabId
   */
  #writeActiveTabState(tabId) {
    const validTabId = this.#getValidTabId(tabId);
    if (!validTabId) return;

    this.#lastActiveTab = validTabId;

    try {
      sessionStorage.setItem(this.#getSessionKey(ACTIVE_TAB_STATE_KEY), validTabId);
    } catch {
      // The private property remains the fallback for this document.
    }
  }

  /**
   * @returns {string}
   */
  #readActiveTabState() {
    try {
      return this.#getValidTabId(sessionStorage.getItem(this.#getSessionKey(ACTIVE_TAB_STATE_KEY)) ?? '');
    } catch {
      return this.#getValidTabId(this.#lastActiveTab);
    }
  }

  /**
   * @returns {{ panelId: string, panelStack: string[], tabId: string } | null}
   */
  #readNavigationState() {
    if (this.#lastNavigationState) return this.#lastNavigationState;

    try {
      const storedState = sessionStorage.getItem(this.#getSessionKey(NAVIGATION_STATE_KEY));
      if (!storedState) return null;

      const parsedState = JSON.parse(storedState);
      if (
        typeof parsedState?.panelId !== 'string' ||
        !Array.isArray(parsedState.panelStack) ||
        !parsedState.panelStack.every((panelId) => typeof panelId === 'string') ||
        typeof parsedState.tabId !== 'string'
      ) {
        this.#clearNavigationState();
        return null;
      }

      return parsedState;
    } catch {
      this.#clearNavigationState();
      return null;
    }
  }

  #clearNavigationState() {
    this.#lastNavigationState = null;

    try {
      sessionStorage.removeItem(this.#getSessionKey(NAVIGATION_STATE_KEY));
    } catch {
      // No persistent state to clear when session storage is unavailable.
    }
  }

  /**
   * @param {boolean} [isDismissed]
   */
  #applyHomeBannerVisibility(isDismissed = isHomeBannerClicked()) {
    document.documentElement.classList.toggle(HOME_BANNER_DISMISSED_CLASS, isDismissed);

    const homeBanner = this.querySelector(HOME_BANNER_SELECTOR);
    if (!(homeBanner instanceof HTMLElement)) return;

    if (isDismissed) {
      homeBanner.hidden = true;
      homeBanner.style.setProperty('display', 'none', 'important');
      return;
    }

    homeBanner.hidden = false;
    homeBanner.style.removeProperty('display');
  }

  #resetDrawerState() {
    const homeBannerClicked = isHomeBannerClicked();

    this.#applyHomeBannerVisibility(homeBannerClicked);
    reset(this.refs.details);
    this.#resetPanels(homeBannerClicked);
    this.#restoreNavigationState();

    removeTrapFocus();
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
  window.DarveysMobileScrollLock?.refresh();
}

/**
 * @returns {boolean}
 */
function isHomeBannerClicked() {
  try {
    if (localStorage.getItem(HOME_BANNER_CLICKED_KEY) !== null) return true;
  } catch {}

  return Boolean(getCookie(HOME_CARDS_DISMISSED_COOKIE));
}

function setHomeBannerClicked() {
  try {
    localStorage.setItem(HOME_BANNER_CLICKED_KEY, 'true');
  } catch {
    setCookie(HOME_CARDS_DISMISSED_COOKIE, 'true', HOME_CARDS_COOKIE_MAX_AGE);
  }
}

/**
 * @param {string} name
 * @returns {string}
 */
function getCookie(name) {
  const cookie = document.cookie
    .split('; ')
    .find((item) => item.startsWith(`${name}=`));

  if (!cookie) return '';

  try {
    return decodeURIComponent(cookie.slice(name.length + 1));
  } catch {
    return '';
  }
}

/**
 * @param {string} name
 * @param {string} value
 * @param {number} maxAge
 */
function setCookie(name, value, maxAge) {
  const secureAttribute = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${maxAge}; Path=/; SameSite=Lax${secureAttribute}`;
}
